import React from "react";
import { FormSection, FormField } from "@/components/common/FormSection";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DIAL_CODES, GENDER_OPTIONS } from "@/lib/dialCodes";

const CLINICAL_ROLE_CODES = ["physician", "hah_physician", "nurse", "hah_nurse"];

export default function UserFormFields({
  form, setForm, setField, setTouched, toggleArrayItem, errors, editing,
  roles, facilities, lookups, isClinical, isSaudi, subSpecialtyOpts,
}) {
  return (
    <div className="p-6 space-y-4">
      <FormSection title="General Information" testId="user-form-general" description="Basic identity and contact details.">
        <FormField label="First name" required error={errors.first_name}>
          <Input data-testid="user-first-name" value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} />
        </FormField>
        <FormField label="Second name" hint="Optional middle name.">
          <Input data-testid="user-second-name" value={form.second_name} onChange={(e) => setForm({ ...form, second_name: e.target.value })} />
        </FormField>
        <FormField label="Last name" required error={errors.last_name}>
          <Input data-testid="user-last-name" value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} />
        </FormField>
        <FormField label="Email" required error={errors.email}>
          <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
        </FormField>
        <FormField label="Gender" required error={errors.gender}>
          <Select value={form.gender} onValueChange={(v) => setField("gender", v)}>
            <SelectTrigger data-testid="user-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
            <SelectContent>
              {GENDER_OPTIONS.map((g) => (
                <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Date of birth" required error={errors.dob} hint="Future dates are not allowed.">
          <Input data-testid="user-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={form.dob} onChange={(e) => setField("dob", e.target.value)} />
        </FormField>
        <FormField label="Years of experience" required error={errors.years_of_experience}>
          <Input data-testid="user-years-experience" type="number" min="0" max="70" value={form.years_of_experience} onChange={(e) => setField("years_of_experience", e.target.value)} />
        </FormField>
        <FormField label="Phone" full error={errors.dial_code} hint="Select the dial code first, then enter the number.">
          <div className="flex gap-2">
            <Select value={form.dial_code} onValueChange={(v) => setField("dial_code", v)}>
              <SelectTrigger data-testid="user-dial-code" className="w-[130px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
              <SelectContent>
                {DIAL_CODES.map((d) => (
                  <SelectItem key={`${d.code}-${d.country}`} value={d.code}>{d.code} · {d.country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid="user-phone"
              className="flex-1"
              disabled={!form.dial_code}
              placeholder={!form.dial_code ? "Select dial code first" : "Phone number"}
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
            />
          </div>
        </FormField>
        <FormField label="Alternate contact number" full error={errors.alternate_dial_code} hint="Optional secondary contact.">
          <div className="flex gap-2">
            <Select value={form.alternate_dial_code} onValueChange={(v) => setField("alternate_dial_code", v)}>
              <SelectTrigger data-testid="user-alt-dial-code" className="w-[130px] shrink-0"><SelectValue placeholder="Code" /></SelectTrigger>
              <SelectContent>
                {DIAL_CODES.map((d) => (
                  <SelectItem key={`${d.code}-${d.country}`} value={d.code}>{d.code} · {d.country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid="user-alt-phone"
              className="flex-1"
              disabled={!form.alternate_dial_code}
              placeholder={!form.alternate_dial_code ? "Select dial code first" : "Alternate number"}
              value={form.alternate_phone}
              onChange={(e) => setField("alternate_phone", e.target.value)}
            />
          </div>
        </FormField>
        <FormField label="Title">
          <Input data-testid="user-title" placeholder="e.g., MD, RN, BSN" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </FormField>
        <FormField label="Department">
          <Input data-testid="user-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
        </FormField>
      </FormSection>

      <FormSection title="Assignment" testId="user-form-assignment" description="Roles and facility assignments. A user can hold multiple roles and belong to multiple facilities.">
        <FormField label="Roles" required full error={errors.role_ids} hint="Select one or more roles.">
          <div data-testid="user-roles-list" className="border border-[#E5E7EB] rounded-md bg-white max-h-[180px] overflow-y-auto divide-y divide-[#F1F5F9]">
            {roles.map((r) => {
              const checked = (form.role_ids || []).includes(r.id);
              return (
                <label key={r.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F9FAFB]">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleArrayItem("role_ids", r.id)}
                    data-testid={`user-role-${r.code}`}
                  />
                  <span className="text-sm text-[#1F2937]">{r.name}</span>
                  {CLINICAL_ROLE_CODES.includes(r.code) && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-[#0B7E8C] bg-[#EAF8FA] px-1.5 py-0.5 rounded">Clinical</span>
                  )}
                </label>
              );
            })}
          </div>
        </FormField>
        <FormField label="Status">
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Facilities" full error={errors.facility_ids} hint="Select one or more facilities.">
          <div data-testid="user-facilities-list" className="border border-[#E5E7EB] rounded-md bg-white max-h-[180px] overflow-y-auto divide-y divide-[#F1F5F9]">
            {facilities.map((f) => (
              <label key={f.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F9FAFB]">
                <Checkbox
                  checked={(form.facility_ids || []).includes(f.id)}
                  onCheckedChange={() => toggleArrayItem("facility_ids", f.id)}
                  data-testid={`user-facility-${f.id}`}
                />
                <span className="text-sm text-[#1F2937]">{f.name}</span>
              </label>
            ))}
          </div>
        </FormField>
      </FormSection>

      <FormSection title="Identity" testId="user-form-identity" description="Nationality and government identity. Identity type must be selected before the identity number.">
        <FormField label="Nationality" required error={errors.nationality}>
          <Select
            value={form.nationality}
            onValueChange={(v) => {
              // When Saudi is selected, lock identity type to National Number
              setForm((f) => {
                const next = { ...f, nationality: v };
                if (v.toLowerCase() === "saudi") next.identity_type = "National Number";
                return next;
              });
              setTouched((t) => ({ ...t, nationality: true, identity_type: true }));
            }}
          >
            <SelectTrigger data-testid="user-nationality"><SelectValue placeholder="Select nationality" /></SelectTrigger>
            <SelectContent>
              {(lookups.nationality || []).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Identity type" error={errors.identity_type} hint={isSaudi ? "Locked to National Number for Saudi nationals." : "Select before entering the identity number."}>
          <Select
            value={form.identity_type}
            disabled={isSaudi}
            onValueChange={(v) => setField("identity_type", v)}
          >
            <SelectTrigger data-testid="user-identity-type"><SelectValue placeholder="Select identity type" /></SelectTrigger>
            <SelectContent>
              {(lookups.identity_type || []).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Identity number" full error={errors.identity_number} hint={isSaudi && form.identity_type === "National Number" ? "Must start with 1 and be exactly 10 digits." : "Unique across the platform."}>
          <Input
            data-testid="user-identity-number"
            disabled={!form.identity_type}
            placeholder={!form.identity_type ? "Select identity type first" : ""}
            value={form.identity_number}
            onChange={(e) => setField("identity_number", e.target.value.trim())}
          />
        </FormField>
      </FormSection>

      {isClinical && (
        <FormSection title="Professional Details" testId="user-form-professional" description="Required for physicians and nurses.">
          <FormField label="License number" required error={errors.license_number} hint="Unique across the platform.">
            <Input data-testid="user-license-number" value={form.license_number} onChange={(e) => setField("license_number", e.target.value.trim())} />
          </FormField>
          <FormField label="License expiry date" required error={errors.license_expiry_date}>
            <Input data-testid="user-license-expiry" type="date" value={form.license_expiry_date} onChange={(e) => setField("license_expiry_date", e.target.value)} />
          </FormField>
          <FormField label="Specialty type" required error={errors.specialty_type}>
            <Select value={form.specialty_type} onValueChange={(v) => setField("specialty_type", v)}>
              <SelectTrigger data-testid="user-specialty-type"><SelectValue placeholder="Select specialty type" /></SelectTrigger>
              <SelectContent>
                {(lookups.specialty_type || []).map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Medical specialization" required full error={errors.medical_specializations || errors.medical_specialization} hint="Select one or more medical specializations.">
            <div data-testid="user-medical-specializations-list" className="border border-[#E5E7EB] rounded-md bg-white max-h-[180px] overflow-y-auto divide-y divide-[#F1F5F9]">
              {(lookups.medical_specialization || []).map((o) => {
                const checked = (form.medical_specializations || []).includes(o.value);
                return (
                  <label key={o.value} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F9FAFB]">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        setForm((f) => {
                          const arr = f.medical_specializations || [];
                          const next = arr.includes(o.value) ? arr.filter((v) => v !== o.value) : [...arr, o.value];
                          // Drop sub-specialties whose parent is no longer selected
                          const allowedParents = new Set(next);
                          const subOpts = lookups.sub_specialty || [];
                          const keptSubs = (f.sub_specialties || []).filter((sv) => {
                            const opt = subOpts.find((x) => x.value === sv);
                            return !opt || !opt.parent_value || allowedParents.has(opt.parent_value);
                          });
                          return { ...f, medical_specializations: next, sub_specialties: keptSubs };
                        });
                        setTouched((t) => ({ ...t, medical_specializations: true, sub_specialties: true }));
                      }}
                      data-testid={`user-medical-specialization-${o.value}`}
                    />
                    <span className="text-sm text-[#1F2937]">{o.label}</span>
                  </label>
                );
              })}
            </div>
          </FormField>
          <FormField label="Sub specialty" required full error={errors.sub_specialties || errors.sub_specialty} hint={!(form.medical_specializations || []).length ? "Select a medical specialization first." : "Select one or more sub specialties."}>
            <div data-testid="user-sub-specialties-list" className="border border-[#E5E7EB] rounded-md bg-white max-h-[180px] overflow-y-auto divide-y divide-[#F1F5F9]">
              {subSpecialtyOpts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-[#6B7280]">
                  {(form.medical_specializations || []).length ? "No sub specialties available for the selected specialization(s)." : "Select a medical specialization first."}
                </div>
              ) : subSpecialtyOpts.map((o) => {
                const checked = (form.sub_specialties || []).includes(o.value);
                return (
                  <label key={o.value} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F9FAFB]">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleArrayItem("sub_specialties", o.value)}
                      data-testid={`user-sub-specialty-${o.value}`}
                    />
                    <span className="text-sm text-[#1F2937]">{o.label}</span>
                    {o.parent_value && (
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-[#6B7280] bg-[#F3F4F6] px-1.5 py-0.5 rounded">{o.parent_value}</span>
                    )}
                  </label>
                );
              })}
            </div>
          </FormField>
        </FormSection>
      )}

      {!editing && (
        <FormSection title="Configuration" testId="user-form-config" description="Initial credentials.">
          <FormField label="Initial password" full hint="The user will be asked to change this on first login.">
            <Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </FormField>
        </FormSection>
      )}

      {editing && (
        <FormSection title="Audit Information" testId="user-form-audit" description="System-generated metadata.">
          <FormField label="Created">
            <Input disabled value={editing.created_at ? new Date(editing.created_at).toLocaleString() : "—"} />
          </FormField>
          <FormField label="Last login">
            <Input disabled value={editing.last_login_at ? new Date(editing.last_login_at).toLocaleString() : "—"} />
          </FormField>
        </FormSection>
      )}
    </div>
  );
}
